import { useState, useEffect, useRef } from "react";
import { supabase } from "./lib/supabaseClient";
import { LanguageProvider } from "./components/LanguageContext";
import { useLanguage } from "./components/LanguageContext";
import { UserProvider } from "./components/UserContext";
import PublicLayout from "./components/PublicLayout";
import PortalLayout from "./components/PortalLayout";
import BrandDashboard from "./components/BrandDashboard";
import BrandSetupWizard from "./components/BrandSetupWizard";

function AppInner() {
  // /geo-wissen is a public, real-URL page that must render immediately
  // without waiting for the async Supabase session check — crawlers need
  // the content right away, and Geo-Wissen has no auth dependency at all.
  const isGeoWissen = window.location.pathname.includes("/geo-wissen");

  const [session, setSession] = useState(undefined);
  const [editingBrand, setEditingBrand] = useState(null);
  const [showWizardFromEmpty, setShowWizardFromEmpty] = useState(false);
  const [showWizardFromSidebar, setShowWizardFromSidebar] = useState(false);
  const [autoOpenBrandId, setAutoOpenBrandId] = useState(null);
  const refreshBrandsRef = useRef(null); // Ref so Wizard onCreated can call it
  const { t } = useLanguage();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      const existingSession = data.session;
      if (existingSession) {
        const hasPersist = localStorage.getItem("georev_persist") === "1";
        const hasActive  = sessionStorage.getItem("georev_active") === "1";
        if (!hasPersist && !hasActive) {
          // No valid flag — sign out silently, show public homepage
          supabase.auth.signOut().then(() => setSession(null));
          return;
        }
        if (hasPersist) {
          // "Stay logged in" was set — session exists but show public homepage first.
          // User will be forwarded to portal when they click "Anmelden".
          setSession(null);
          return;
        }
      }
      setSession(existingSession);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (isGeoWissen) {
    return <PublicLayout adminMode={false} planFromUrl={null} />;
  }

  // Kurz warten bis Session geladen — verhindert weisse Seite / Flackern
  if (session === undefined) {
    return (
      <div style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f8fafc",
        fontFamily: "DM Sans, sans-serif",
        color: "#94a3b8",
        fontSize: 14,
      }}>
        Loading…
      </div>
    );
  }

  // Nicht eingeloggt → PublicLayout mit Login/Signup Slide-in
  if (!session) {
    const hash = window.location.hash;
    const pathname = window.location.pathname;
    const planFromUrl = ["trial", "starter", "professional", "agency", "pilot"].find(p =>
      hash.includes(`/register/${p}`) || pathname.includes(`/register/${p}`)
    ) ?? null;
    const adminMode = planFromUrl !== null || window.location.search.includes("register") || pathname.includes("register");

    // If "stay logged in" was used, restore session on Anmelden click (skip login form)
    const handleLoginClick = async () => {
      const hasPersist = localStorage.getItem("georev_persist") === "1";
      if (hasPersist) {
        // Refresh the session to get a valid access token (stored token may be expired)
        const { data } = await supabase.auth.refreshSession();
        if (data.session) {
          setSession(data.session);
          return true; // session restored — signal to skip auth modal
        }
        // Refresh failed (refresh token expired) — clear flag, show login form
        localStorage.removeItem("georev_persist");
      }
      return false; // no persisted session — open auth modal
    };

    return <PublicLayout adminMode={adminMode} planFromUrl={planFromUrl} onLoginClick={handleLoginClick} />;
  }

  // Eingeloggt → Portal
  return (
    <UserProvider session={session}>
    <PortalLayout session={session} onShowWizard={() => setShowWizardFromSidebar(true)}>
      {(brand, { refreshBrands }) => {
        // Store in ref so Wizard handlers (outside render prop) can access it
        refreshBrandsRef.current = refreshBrands;

        if (!brand) return (
          <div style={{
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            height: "60vh", color: "#94a3b8", gap: 16,
            fontFamily: "'new-science','DM Sans',sans-serif"
          }}>
            <div style={{fontSize: 48}}>🏷️</div>
            <div style={{fontSize: 18, fontWeight: 600, color: "#0f172a"}}>Keine Marke ausgewählt</div>
            <div style={{fontSize: 14, color: "#64748b", marginBottom: 8}}>Wähle eine Marke in der Sidebar oder erstelle eine neue.</div>
            <button onClick={() => setShowWizardFromEmpty(true)} style={{
              background: "#2563eb", color: "white", border: "none",
              padding: "12px 24px", borderRadius: "8px", fontSize: "14px",
              fontWeight: 600, cursor: "pointer", fontFamily: "'new-science','DM Sans',sans-serif",
              boxShadow: "0 2px 8px rgba(37,99,235,0.25)", transition: "all 0.15s"
            }} onMouseOver={e => e.target.style.background = "#1d4ed8"}
               onMouseOut={e => e.target.style.background = "#2563eb"}>
              🚀 Jetzt Marke erstellen
            </button>
          </div>
        );
        return (
          <BrandDashboard
            brand={brand}
            onEdit={(b) => setEditingBrand(b)}
            autoOpenBrandId={autoOpenBrandId}
            onAutoOpenConsumed={() => setAutoOpenBrandId(null)}
            onDelete={(deletedId) => {
              console.log("Brand deleted:", deletedId);
              if (refreshBrands) {
                setTimeout(() => refreshBrands(), 100);
              }
            }}
          />
        );
      }}
    </PortalLayout>

    {/* Edit Brand Wizard */}
    {editingBrand && (
      <BrandSetupWizard
        userId={session.user.id}
        brandToEdit={editingBrand}
        onClose={() => setEditingBrand(null)}
        onCreated={(brandId, savedTopics, savedTerms) => {
          setEditingBrand(null);
          setAutoOpenBrandId(brandId);
          if (refreshBrandsRef.current) refreshBrandsRef.current(brandId);
        }}
      />
    )}

    {showWizardFromSidebar && (
      <BrandSetupWizard
        userId={session.user.id}
        onClose={() => setShowWizardFromSidebar(false)}
        onCreated={(brandId, savedTopics, savedTerms) => {
          setShowWizardFromSidebar(false);
          setAutoOpenBrandId(brandId);
          if (refreshBrandsRef.current) refreshBrandsRef.current(brandId);
        }}
      />
    )}

    {showWizardFromEmpty && (
      <BrandSetupWizard
        userId={session.user.id}
        onClose={() => setShowWizardFromEmpty(false)}
        onCreated={(brandId, savedTopics, savedTerms) => {
          setShowWizardFromEmpty(false);
          setAutoOpenBrandId(brandId);
          if (refreshBrandsRef.current) refreshBrandsRef.current(brandId);
        }}
      />
    )}
    </UserProvider>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AppInner />
    </LanguageProvider>
  );
}

export default App;
